import React from 'reactn';
import { useSelector } from 'react-redux';
import useSWR from 'swr';
import { Renew16 } from '@carbon/icons-react';
import { createStructuredSelector } from 'reselect';
import { Loading } from 'carbon-components-react';

import SummaryCard from '../../components/card/summary-card/summary-card.component';
import { TrendingUp, PieChart, Users } from 'react-feather';
import { withRouter } from 'react-router';
import {
  formatCurrency,
  hasSubscriptionAccess,
  isAdmin,
  isWithinOneWeek,
  redirectToBillingService,
} from '../../utils/common.util';
import { apiBaseUrl } from '../../api/common';
import { selectCurrentUser } from '../../redux/selectors/user.selectors';

import useFetchData from '../../utils/useFetchData';

import { useEffect, useState } from 'react';

const SummaryPane = (props) => {
  const currentUser = useSelector(selectCurrentUser);
  const token = currentUser?.authToken;

  const [revalidate, setRevalidate] = useState(true);

  const { data: revenue } = useSWR(`dashboard/monthly-stats/revenue`, {
    revalidateOnFocus: false,
    revalidateOnMount: revalidate,
    refreshInterval: 1800000, //30 minutes
  });

  const { data: expenditure } = useSWR(`dashboard/monthly-stats/expenditure`, {
    revalidateOnFocus: false,
    revalidateOnMount: revalidate,
    refreshInterval: 1800000, //30 minutes
  });

  const { data: students } = useSWR(`dashboard/students/total-enrolled`, {
    revalidateOnFocus: false,
    revalidateOnMount: revalidate,
    refreshInterval: 1800000, //30 minutes
  });

  const { data: totalBalanceDue, mutate } = useSWR(
    `dashboard/students/amount-owed`,
    {
      revalidateOnFocus: false,
      revalidateOnMount: revalidate,
      refreshInterval: 1800000, //30 minutes
    },
  );

  const { data: recentInvoice } = useFetchData(
    `school/retrieve-recent-invoice?schoolId=${currentUser?.school?.id}`,
    { enabled: !!currentUser?.school?.id },
  );

  // State to track loading
  const [isLoading, setIsLoading] = useState(false);

  const updateAmountOwed = async () => {
    setIsLoading(true);
    try {
      // Call the new endpoint to create the record
      const response = await fetch(
        `${apiBaseUrl}billing/create-student-amount-owed-record`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (!response.ok) throw new Error('Failed to update student amount owed');
      // On success, trigger re-fetch of the current endpoint
      await mutate(); // Re-fetches dashboard/students/amount-owed
    } catch (error) {
      console.error('Error updating student amount owed:', error);
      // Optionally, show an error message to the user
    } finally {
      setIsLoading(false); // Stop loading after both requests complete
    }
  };

  const renderValue = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      {totalBalanceDue
        ? formatCurrency(totalBalanceDue.totalStudentsBalanceDue)
        : '...'}
      <button
        onClick={updateAmountOwed}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: 0,
          margin: 0,
        }}
        disabled={isLoading} // Disable button while loading
      >
        {isLoading ? (
          <Loading small withOverlay={false} /> // Show loader during loading
        ) : (
          <Renew16 size={16} /> // Show refresh icon when not loading
        )}
      </button>
    </div>
  );

  return (
    <div>
      {isAdmin(props.currentUser) && (
        <div>
          <h3 className="text-dark text-24 font-bold">At a glance</h3>
          <p className="text-14 text-grey mt-10">
            Summary of some metrics over the months
          </p>

          {hasSubscriptionAccess(currentUser, [
            'SubscriptionAccessReadOnly',
          ]) && (
            <>
              {(recentInvoice?.invoice?.status?.toUpperCase() === 'UNPAID' ||
                recentInvoice?.invoice?.status?.toUpperCase() ===
                  'OVERDUE') && (
                <div
                  className="font-bold"
                  style={{
                    padding: '12px 15px',
                    borderRadius: '12px',
                    background: isWithinOneWeek(
                      recentInvoice?.invoice?.grace_period_end_date,
                    )
                      ? '#da1e28'
                      : '#164d9d',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '15px',
                    margin: '20px 0',
                    width: 'fit-content',
                  }}>
                  <p>Subscription for this Term is available.</p>
                  {hasSubscriptionAccess(currentUser) && (
                    <div
                      onClick={() =>
                        redirectToBillingService(
                          `invoices/${recentInvoice?.invoice?.id}`,
                          token,
                        )
                      }
                      style={{
                        padding: '8px',
                        borderRadius: '12px',
                        color: 'white',
                        border: '2px solid white',
                        cursor: 'pointer',
                      }}>
                      Pay Now
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          <div className="bx--row mt-10">
            <div className="bx--col-md-2 fade-in">
              <SummaryCard
                description="Cash Received this Month"
                iconBackgroundColor="green"
                transform={true}
                value={
                  revenue
                    ? formatCurrency(revenue.totalRevenuesForTheMonth)
                    : '...'
                }
                leftAction={props.onAddCashReceive}
                rightActionText="GENERATE REPORT"
                rightAction={() =>
                  props.history.push('analytics/cash-received')
                }>
                <TrendingUp color="#fff" />
              </SummaryCard>
            </div>

            <div className="bx--col-md-2 fade-in">
              <SummaryCard
                // description="Expenses this Month"
                description="Cash Paid This Month"
                value={
                  expenditure
                    ? formatCurrency(expenditure.totalExpenditureForTheMonth)
                    : '...'
                }
                iconBackgroundColor="red"
                leftAction={props.onAddExpenses}
                rightActionText="GENERATE REPORT"
                rightAction={() => props.history.push('analytics/cash-spent')}>
                <TrendingUp color="#fff" />
              </SummaryCard>
            </div>

            <div className="bx--col-md-2 fade-in">
              <SummaryCard
                description="Amount owed by students"
                value={renderValue()}
                iconBackgroundColor="orange"
                leftActionText="VIEW"
                dangerValue={totalBalanceDue ? true : false}
                leftAction={() => props.history.push('student/?in_debt=True')}
                rightActionText="GENERATE REPORT"
                rightAction={() =>
                  props.history.push('analytics/debtors-list')
                }>
                <PieChart color="#fff" />
              </SummaryCard>
            </div>

            <div className="bx--col-md-2 fade-in">
              <SummaryCard
                description="Students enrolled"
                value={students ? students.totalEnrolledStudents : '...'}
                hideRightAction={true}
                addColor={true}
                leftActionText="VIEW"
                leftAction={() => props.history.push('/student')}>
                <Users />
              </SummaryCard>
            </div>
          </div>

          {/* <LoaderPane loading={isValidating} */}
          {/* error={!data && error?.message && "Summary failed to update. " } onReload={()=>{ mutate()}}/>*/}
          {/*{(!isValidating) && <div className="text-primary font-bold text-center pointer" onClick={()=>{mutate() }}>Refresh summary</div>}*/}

          <hr className="grey-hr mt-20" />
        </div>
      )}
    </div>
  );
};

// this will provide us with the state values we need
const mapStateToProps = createStructuredSelector({
  currentUser: selectCurrentUser,
});

export default withRouter(SummaryPane);
